import React from "react";
import { Link } from "react-router-dom";

function H2({ children, id }) {
  return (
    <h2 id={id} className="text-xl font-medium text-zinc-100 mt-8 mb-3 scroll-mt-20">
      {children}
    </h2>
  );
}
function P({ children }) {
  return <p className="text-sm text-zinc-300 leading-relaxed">{children}</p>;
}

export default function DatenschutzPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6 px-2 py-2" data-testid="datenschutz-page">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Rechtliches</p>
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-100 sm:text-5xl">Datenschutzerklärung</h1>
        <p className="text-sm text-zinc-400">
          DSGVO-konforme Information über die Verarbeitung personenbezogener Daten bei DealAlert (
          <a className="text-emerald-400 hover:underline" href="https://dealalert.ai">https://dealalert.ai</a>
          ). Stand: Juni 2026.
        </p>
      </header>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="verantwortlich">1. Verantwortlicher</H2>
        <P>
          Cloud Service International j.d.o.o., Zagreb, Kroatien (HR). Kontakt:{" "}
          <a className="text-emerald-400 hover:underline" href="mailto:info@dealalert.ai">info@dealalert.ai</a>.
          Vollständige Angaben siehe <Link className="text-emerald-400 hover:underline" to="/impressum">Impressum</Link>.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="account">2. Account-Daten</H2>
        <P>
          Bei der Registrierung verarbeiten wir: E-Mail-Adresse, Vor- und Nachname (optional Telefonnummer),
          gehashtes Passwort (bcrypt), Zeitpunkt der Registrierung, IP-Adresse, User-Agent. Rechtsgrundlage:
          Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
        </P>
        <P>
          Speicherdauer: bis zur Kontolöschung. Die Löschung kann jederzeit per E-Mail an{" "}
          <a className="text-emerald-400 hover:underline" href="mailto:info@dealalert.ai">info@dealalert.ai</a>{" "}
          beantragt werden — wir löschen innerhalb von 30 Tagen, sofern keine gesetzlichen Aufbewahrungsfristen
          (z. B. § 132 BAO für Stripe-Rechnungen, 7 Jahre) entgegenstehen.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="email">3. E-Mail-Versand via Resend</H2>
        <P>
          Für transaktionale E-Mails (Account-Verifizierung, Auszahlungs-Benachrichtigungen) verwenden wir den
          Dienst <em>Resend</em> (Resend.com, USA). Übermittelt werden: deine E-Mail-Adresse, dein Name (sofern
          angegeben), der Inhalt der jeweiligen Nachricht.
        </P>
        <P>
          Resend ist nach EU-US Data Privacy Framework zertifiziert; zusätzlich besteht ein Auftragsverarbeitungs-
          vertrag. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) bzw. Art. 6 Abs. 1 lit. f DSGVO
          (berechtigtes Interesse an zuverlässiger Zustellung).
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="stripe">4. Zahlungsabwicklung via Stripe</H2>
        <P>
          Für Abonnement-Zahlungen nutzen wir Stripe Payments Europe Limited (Irland). Übermittelt werden:
          E-Mail-Adresse, IP-Adresse, Zahlungsmittel-Daten (Kreditkarte/SEPA — direkt an Stripe, NICHT an uns).
          Wir speichern nur die Stripe-Kunden-ID, das Abo-Datum und den Zahlungsstatus.
        </P>
        <P>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Aufbewahrungsfrist Rechnungen: 7 Jahre gemäß § 132 BAO bzw.
          § 147 AO. Stripe Datenschutz:{" "}
          <a className="text-emerald-400 hover:underline" href="https://stripe.com/at/privacy" target="_blank" rel="noopener noreferrer">
            stripe.com/at/privacy
          </a>.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="ai">5. KI-Dienste (Anthropic Claude)</H2>
        <P>
          DealAlert nutzt <strong>Anthropic Claude</strong> (Anthropic PBC, USA — Modell: Claude Sonnet 4.6), um dir
          KI-basierte Analysen zu Inseraten zu generieren. Übermittelte Daten pro Anfrage können sein:
        </P>
        <ul className="list-disc pl-6 text-sm text-zinc-300 space-y-1">
          <li>deine KI-Frage</li>
          <li>Titel und Beschreibung des Inserats</li>
          <li>Eckdaten (Preis, Baujahr, Größe, Zustand, etc.)</li>
          <li>ungefährer Standort (Postleitzahl / Bezirk), falls verfügbar</li>
        </ul>
        <P>
          <strong>Zweck:</strong> ausschließlich Generierung der von dir angeforderten KI-Antwort. Es wird{" "}
          <strong>kein Werbeprofil</strong> aus diesen Daten erstellt und Anthropic verwendet die Daten nicht zum
          Trainieren ihrer Modelle (Zero-Retention-Konfiguration).
        </P>
        <P>
          <strong>Consent:</strong> In der iOS-App ist die KI-Verarbeitung standardmäßig deaktiviert. Beim ersten Öffnen
          des KI-Beraters erscheint ein Dialog, in dem du der Verarbeitung ausdrücklich zustimmen musst. Du kannst deine
          Einwilligung jederzeit unter <em>Profil → KI-Verarbeitung</em> widerrufen. Bei deaktivierter Verarbeitung
          verlassen keinerlei Inserat- oder Nutzungsdaten dein Gerät in Richtung Anthropic.
        </P>
        <P>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) i.V.m. Art. 6 Abs. 1 lit. b DSGVO
          (Vertragserfüllung). Ein Auftragsverarbeitungs-Vertrag (DPA) mit Anthropic ist abgeschlossen. Datenübermittlung
          in die USA erfolgt auf Grundlage der EU-Standardvertragsklauseln. Anthropic Datenschutz:{" "}
          <a className="text-emerald-400 hover:underline" href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">
            anthropic.com/legal/privacy
          </a>.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="referral">6. Referral-Programm & Auszahlungs-Daten</H2>
        <P>
          Wenn du am Share-&-Earn-Programm teilnimmst und eine Auszahlung anforderst, speichern wir IBAN,
          Kontoinhaber-Name und Notiz. Die IBAN wird verschlüsselt (Fernet/AES-128) in unserer Datenbank
          abgelegt und ausschließlich zur Auszahlungsausführung verwendet.
        </P>
        <P>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Aufbewahrungsfrist: 7 Jahre (Buchhaltung).
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="cookies">7. Cookies / lokale Speicherung</H2>
        <P>
          Wir setzen <strong>keine</strong> Tracking-Cookies. Für die Authentifizierung speichern wir
          JWT-Tokens im <em>localStorage</em> deines Browsers — diese sind technisch notwendig und werden bei
          Logout entfernt. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Funktionalität).
        </P>
        <P>
          Optional: PostHog wird zur Produktanalyse eingesetzt (anonymisierte Session-Aufzeichnung,
          keine Tracking-Cookies, IP wird gekürzt). PostHog-Daten werden in EU-Rechenzentren gespeichert.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="hosting">8. Hosting</H2>
        <P>
          Die Anwendung wird auf Kubernetes-Clustern in der EU betrieben. MongoDB wird in der EU gehostet.
          Server-Logs (IP, Pfad, Statuscode, Zeitstempel) werden zur Fehlerdiagnose 30 Tage aufbewahrt und
          dann automatisch gelöscht.
        </P>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-3">
        <H2 id="rights">9. Deine Rechte gemäß DSGVO</H2>
        <P>Du hast jederzeit das Recht auf:</P>
        <ul className="list-disc list-outside ml-5 text-sm text-zinc-300 space-y-1.5">
          <li>Auskunft über die zu deiner Person gespeicherten Daten (Art. 15)</li>
          <li>Berichtigung unrichtiger Daten (Art. 16)</li>
          <li>Löschung („Recht auf Vergessenwerden", Art. 17)</li>
          <li>Einschränkung der Verarbeitung (Art. 18)</li>
          <li>Datenübertragbarkeit (Art. 20)</li>
          <li>Widerspruch gegen die Verarbeitung (Art. 21)</li>
          <li>Beschwerde bei der Aufsichtsbehörde (z. B. Datenschutzbehörde Österreich,{" "}
            <a className="text-emerald-400 hover:underline" href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer">dsb.gv.at</a>)
          </li>
        </ul>
        <P>
          Zur Ausübung deiner Rechte (insb. Account-Löschung) genügt eine formlose E-Mail an{" "}
          <a className="text-emerald-400 hover:underline" href="mailto:info@dealalert.ai">info@dealalert.ai</a>.
        </P>
      </div>

      <p className="text-xs text-zinc-500">
        Stand: Juni 2026.
      </p>
    </div>
  );
}
